(() => {
  const money = (value) => `PKR ${new Intl.NumberFormat('en-PK', { maximumFractionDigits: 0 }).format(value)}`;
  const roundUp = (value, increment) => Math.ceil((value - 0.000001) / increment) * increment;
  const roundDown = (value, increment) => Math.floor((value + 0.000001) / increment) * increment;
  const formatMetres = (value) => Number(value.toFixed(2)).toString();
  const setActionLabel = (button, label) => {
    const text = button.querySelector?.('[data-look-action-label]');
    if (text) text.textContent = label;
    else button.textContent = label;
  };
  const isStandardCut = (metres, standardMetres) => Math.abs(metres - standardMetres) < 0.000001;
  const readI18n = (studio) => {
    if (studio.saraI18n) return studio.saraI18n;
    const node = studio.querySelector('[data-sara-look-i18n]') || document.querySelector('[data-sara-look-i18n]');
    try { studio.saraI18n = node ? JSON.parse(node.textContent) : {}; }
    catch { studio.saraI18n = {}; }
    return studio.saraI18n;
  };
  const t = (studio, key, fallback = '', vars = {}) => {
    let value = readI18n(studio)[key] || fallback || key;
    for (const [name, replacement] of Object.entries(vars)) {
      value = value.replaceAll(`{{ ${name} }}`, String(replacement)).replaceAll(`{{${name}}}`, String(replacement));
    }
    return value;
  };

  const openScan = (studio, src, label, opener, event) => {
    const overlay = studio.querySelector('[data-look-scan-dialog]');
    const large = overlay?.querySelector('[data-look-scan-large]');
    const caption = overlay?.querySelector('[data-look-scan-caption]');
    const close = overlay?.querySelector('[data-look-scan-close]');
    if (!overlay || !large || !caption || !close || !src) return;
    large.src = src;
    large.alt = t(studio, 'fabric_image_alt', `${label} fabric image, enlarged`, { label });
    caption.textContent = t(studio, 'fabric_closeup', `${label} · Close-up of the actual fabric. Screen colour may differ from the cloth.`, { label });
    studio.saraScanOpener = opener;
    const hover = studio.querySelector('[data-look-scan-hover]');
    if (hover) hover.hidden = true;
    overlay.hidden = false;
    close.focus?.();
    event.preventDefault();
  };

  const showScanHover = (studio, src, label) => {
    const hover = studio.querySelector('[data-look-scan-hover]');
    const image = hover?.querySelector('[data-look-scan-hover-image]');
    const caption = hover?.querySelector('[data-look-scan-hover-caption]');
    if (!hover || !image || !caption || !src || !studio.querySelector('[data-look-scan-dialog]')?.hidden) return;
    image.src = src;
    image.alt = t(studio, 'fabric_hover_alt', `${label} fabric close-up`, { label });
    caption.textContent = label;
    hover.hidden = false;
  };

  const hideScanHover = (studio) => {
    const hover = studio.querySelector('[data-look-scan-hover]');
    if (hover) hover.hidden = true;
  };

  const closeScan = (studio) => {
    const overlay = studio.querySelector('[data-look-scan-dialog]');
    if (!overlay || overlay.hidden) return;
    overlay.hidden = true;
    studio.saraScanOpener?.focus?.();
  };

  const renderPalette = (studio, select, selector) => {
    const host = studio.querySelector(selector);
    if (!host || !select.options) return;
    host.replaceChildren(...[...select.options].map((option, index) => {
      const tile = document.createElement('div');
      tile.className = 'sf-look-studio__scan-tile';
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'sf-look-studio__scan';
      button.dataset.index = String(index);
      button.title = option.value;
      button.setAttribute('aria-label', t(studio, 'choose_fabric', `Choose ${option.value}`, { label: option.value }));
      button.setAttribute('aria-pressed', String(option.selected));
      const image = document.createElement('span');
      image.className = 'sf-look-studio__scan-image';
      image.style.backgroundColor = option.dataset.colour || '#777';
      const thumbnail = option.dataset.thumb || option.dataset.scan;
      if (thumbnail) image.style.backgroundImage = `url("${thumbnail}")`;
      const label = document.createElement('span');
      label.textContent = option.dataset.shade?.toLowerCase().startsWith('representative') ? option.dataset.quality : (option.dataset.shade || option.value);
      button.append(image, label);
      button.addEventListener('click', () => {
        select.selectedIndex = index;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      });
      tile.appendChild(button);
      return tile;
    }));
  };

  const quoteUrl = (baseHref, lines) => {
    const url = new URL(baseHref);
    url.searchParams.set('text', lines.join('\n'));
    return url.toString();
  };

  const lookKey = (look) => JSON.stringify([
    look.pieces, look.custom, look.kValue, look.wValue,
    look.kCount, look.wCount, look.kMetres, look.wMetres
  ]);

  const renderTray = (studio) => {
    const list = studio.querySelector('[data-look-tray-list]');
    if (!list) return;
    const looks = studio.saraSavedLooks || [];
    const empty = studio.querySelector('[data-look-tray-empty]');
    const footer = studio.querySelector('[data-look-tray-footer]');
    const count = studio.querySelector('[data-look-tray-count]');
    const total = studio.querySelector('[data-look-tray-total]');
    const note = studio.querySelector('[data-look-tray-note]');
    const cart = studio.querySelector('[data-look-cart]');
    const enquiry = studio.querySelector('[data-look-tray-enquire]');
    count.textContent = `(${looks.length})`;
    empty.hidden = looks.length > 0;
    footer.hidden = looks.length === 0;
    list.replaceChildren();
    if (!looks.length) {
      studio.saraCartItems = null;
      if (cart) cart.hidden = true;
      const singleEnquire = studio.querySelector('[data-look-enquire]');
      if (singleEnquire) singleEnquire.hidden = false;
      return;
    }

    const allLines = [
      t(studio, 'wa_intro', 'Hello Sara Fabrics, please check whether the selected unstitched fabrics below are currently available before I place an order:'),
      '',
      t(studio, 'wa_heading', 'SELECTED FABRIC ORDER ENQUIRY')
    ];
    let allTotal = 0;
    const cartItems = [];
    let quoteOnly = 0;
    const variantCounts = new Map();
    const variantStock = new Map();
    looks.forEach((look, index) => {
      allTotal += look.total;
      allLines.push(t(studio, 'look_line', `${index + 1}. Look ${index + 1}: ${look.summary}; ${money(look.total)}; ${look.custom ? 'custom' : 'standard'} cut`, {
        n: index + 1,
        summary: look.summary,
        amount: money(look.total),
        cut: look.custom ? t(studio, 'wa_cut_custom_short', 'custom') : t(studio, 'wa_cut_standard_short', 'standard')
      }));
      if (look.cartItems) {
        look.cartItems.forEach((item) => {
          variantCounts.set(item.id, (variantCounts.get(item.id) || 0) + item.quantity);
          variantStock.set(item.id, Math.min(variantStock.get(item.id) ?? Infinity, item.stock));
          const { stock, ...cartItem } = item;
          cartItems.push({ ...cartItem, properties: { ...cartItem.properties, Look: `Look ${index + 1}` } });
        });
      } else quoteOnly += 1;

      const card = document.createElement('li');
      card.className = 'sf-look-studio__look-card';
      if (look.photo) {
        const image = document.createElement('img');
        image.src = look.photo;
        image.alt = look.photoIsScan
          ? t(studio, 'look_photo_scan_alt', `Fabric image for look ${index + 1}; inspect both selected fabric swatches`, { n: index + 1 })
          : t(studio, 'look_photo_model_alt', `Appearance-matched styling render for look ${index + 1}; fabric swatches show the selected cloth`, { n: index + 1 });
        image.width = 90;
        image.height = 130;
        image.loading = 'lazy';
        card.appendChild(image);
      }
      const detail = document.createElement('div');
      detail.className = 'sf-look-studio__look-detail';
      const title = document.createElement('strong');
      title.textContent = t(studio, 'look_title', `Look ${index + 1} · ${money(look.total)}`, { n: index + 1, amount: money(look.total) });
      const description = document.createElement('p');
      description.textContent = look.summary;
      const status = document.createElement('small');
      const enquiryMode = studio.dataset.visualPreview === 'true' && studio.dataset.devCheckout !== 'true';
      status.textContent = enquiryMode
        ? t(studio, 'status_enquiry', 'Ready for WhatsApp enquiry')
        : (look.cartItems
          ? t(studio, 'status_standard', 'Standard cuts · test-cart eligible if stock remains')
          : t(studio, 'status_quote', 'Quote only · custom length or unavailable test units'));
      detail.append(title, description, status);
      const actions = document.createElement('div');
      actions.className = 'sf-look-studio__look-actions';
      for (const [label, action] of [[t(studio, 'edit', 'Edit'), 'edit'], [t(studio, 'remove', 'Remove'), 'remove']]) {
        const button = document.createElement('button');
        button.type = 'button';
        const icon = action === 'remove' ? studio.querySelector('[data-look-icon-remove]')?.content?.cloneNode(true) : null;
        if (icon) button.appendChild(icon);
        const buttonLabel = document.createElement('span');
        buttonLabel.textContent = label;
        button.appendChild(buttonLabel);
        button.dataset.lookAction = action;
        button.dataset.lookIndex = String(index);
        actions.appendChild(button);
      }
      card.append(detail, actions);
      list.appendChild(card);
    });
    allLines.push('', t(studio, 'fabric_total', `Fabric total: ${money(allTotal)}`, { amount: money(allTotal) }), t(studio, 'wa_outro_tray', 'Please confirm availability for each listed quality and shade, then confirm the exact shade, cutting, final price and delivery details. This message is an enquiry, not a confirmed order.'));
    total.textContent = t(studio, 'tray_total', `Saved-look fabric total: ${money(allTotal)}`, { amount: money(allTotal) });
    enquiry.href = quoteUrl(enquiry.dataset.baseHref, allLines);
    const hasConflict = [...variantCounts].some(([id, quantity]) => quantity > variantStock.get(id));
    const checkoutHosts = new Set([
      'sara-fabrics-winter-2026-checkout-lab.myshopify.com',
      'shop.sarafabrics.com',
      'sarafabrics.myshopify.com'
    ]);
    const isLabHost = location.hostname === 'sara-fabrics-winter-2026-checkout-lab.myshopify.com';
    const safeStore = studio.dataset.devCheckout === 'true' && checkoutHosts.has(location.hostname);
    studio.saraCartItems = safeStore && !hasConflict && cartItems.length ? cartItems : null;
    if (cart) {
      cart.hidden = !safeStore || cartItems.length === 0;
      cart.disabled = Boolean(studio.saraSubmitting || hasConflict);
      cart.setAttribute?.('aria-disabled', String(hasConflict));
      const lookWord = looks.length - quoteOnly === 1
        ? t(studio, 'look_word_one', 'look')
        : t(studio, 'look_word_other', 'looks');
      const cartLabel = t(studio, 'cart_looks', `Add ${looks.length - quoteOnly} standard ${lookWord} to cart`, {
        count: looks.length - quoteOnly,
        look_word: lookWord
      });
      setActionLabel(cart, `${isLabHost ? 'DEV TEST · ' : ''}${cartLabel}`);
    }
    const singleEnquire = studio.querySelector('[data-look-enquire]');
    if (singleEnquire) singleEnquire.hidden = looks.length > 0;
    if (note) {
      note.textContent = hasConflict
        ? t(studio, 'conflict', 'The saved looks exceed the available units for a shared fabric cut. Change a look or request all looks on WhatsApp; nothing has been added to the cart.')
        : quoteOnly
          ? (studio.dataset.devCheckout === 'true'
            ? t(studio, 'quote_only', `${quoteOnly} look(s) need a manual quote and will NOT be added to the cart. The button adds only the eligible standard looks.`, { count: quoteOnly })
            : t(studio, 'quote_only_enquiry', 'Saved combinations are ready. Continue shopping to add another, or send all of them on WhatsApp below.', { count: quoteOnly }))
          : (studio.dataset.devCheckout === 'true'
            ? t(studio, 'all_standard', 'All saved looks are standard cuts. Availability is checked again by Shopify when they are added to the cart.')
            : t(studio, 'all_ready_enquiry', 'All saved combinations are ready. Continue shopping to add another, or send them on WhatsApp below.'));
    }
  };

  const update = (studio) => {
    const k = studio.querySelector('[data-look-kameez]');
    const w = studio.querySelector('[data-look-waistcoat]');
    const summary = studio.querySelector('[data-look-summary]');
    const prices = studio.querySelector('[data-look-prices]');
    const enquiry = studio.querySelector('[data-look-enquire]');
    const error = studio.querySelector('[data-look-error]');
    const lengths = studio.querySelector('[data-look-lengths]');
    const cart = studio.querySelector('[data-look-cart]');
    const save = studio.querySelector('[data-look-save]');
    const kLength = studio.querySelector('[data-look-kameez-length]');
    const wLength = studio.querySelector('[data-look-waistcoat-length]');
    const kCountInput = studio.querySelector('[data-look-kameez-count]');
    const wCountInput = studio.querySelector('[data-look-waistcoat-count]');
    const pieces = studio.querySelector('[data-look-pieces]:checked')?.value || 'both';
    const custom = studio.querySelector('[data-look-cut]:checked')?.value === 'custom';
    studio.saraCartItems = null;
    studio.saraDraft = null;
    if (cart && !studio.querySelector('[data-look-tray-list]')) cart.hidden = true;
    if (!k || !w || !summary || !prices || !enquiry || !error || !lengths || !kLength || !wLength || !k.selectedOptions.length || !w.selectedOptions.length) return;

    const needK = pieces !== 'waistcoat';
    const needW = pieces !== 'suit';
    const previewK = needK || ((studio.dataset.devCheckout === 'true' || studio.dataset.visualPreview === 'true') && needW);
    const kOption = k.selectedOptions[0];
    const wOption = w.selectedOptions[0];
    k.disabled = !previewK;
    w.disabled = !needW;
    k.closest('.sf-look-studio__field').hidden = !previewK;
    w.closest('.sf-look-studio__field').hidden = !needW;
    const kHeading = studio.querySelector('[data-look-kameez-heading]');
    if (kHeading) kHeading.textContent = needK
      ? t(studio, 'kameez_heading', 'Shalwar kameez fabric')
      : t(studio, 'kameez_heading_preview', 'Preview shalwar kameez colour (not included)');
    const kPalette = studio.querySelector('[data-look-kameez-palette]');
    const wPalette = studio.querySelector('[data-look-waistcoat-palette]');
    if (kPalette) kPalette.hidden = !previewK;
    if (wPalette) wPalette.hidden = !needW;
    lengths.hidden = false;
    kLength.disabled = !custom || !needK;
    wLength.disabled = !custom || !needW;
    for (const input of [kLength, wLength]) input.closest('.sf-look-studio__stepper')?.querySelectorAll?.('button').forEach((button) => { button.disabled = input.disabled; });
    kLength.closest('.sf-look-studio__field').hidden = !needK;
    wLength.closest('.sf-look-studio__field').hidden = !needW;
    if (kCountInput) kCountInput.closest('.sf-look-studio__field').hidden = !needK;
    if (wCountInput) wCountInput.closest('.sf-look-studio__field').hidden = !needW;
    studio.classList.toggle('sf-look-studio--suit-only', !needW);

    const kColour = kOption.dataset.colour;
    const wColour = wOption.dataset.colour;
    studio.style.setProperty('--sf-look-kameez', previewK ? kColour : '#d3c4af');
    studio.style.setProperty('--sf-look-waistcoat', wColour);
    const kSwatch = studio.querySelector('[data-look-kameez-swatch]');
    const wSwatch = studio.querySelector('[data-look-waistcoat-swatch]');
    const kThumbnail = kOption.dataset.thumb || kOption.dataset.scan;
    const wThumbnail = wOption.dataset.thumb || wOption.dataset.scan;
    if (kSwatch) {
      kSwatch.style.backgroundColor = kColour || '';
      kSwatch.style.backgroundImage = kThumbnail ? `url("${kThumbnail}")` : '';
      kSwatch.hidden = !previewK;
    }
    if (wSwatch) {
      wSwatch.style.backgroundColor = wColour || '';
      wSwatch.style.backgroundImage = wThumbnail ? `url("${wThumbnail}")` : '';
      wSwatch.hidden = !needW;
    }
    const reviewItems = [
      {
        show: previewK,
        option: kOption,
        card: studio.querySelector('[data-look-review-kameez]'),
        image: studio.querySelector('[data-look-review-kameez-img]'),
        name: studio.querySelector('[data-look-review-kameez-name]'),
        button: studio.querySelector('[data-look-review-kameez-btn]'),
        role: t(studio, 'selected_kameez', 'Shalwar kameez'),
        thumb: kThumbnail,
        colour: kColour
      },
      {
        show: needW,
        option: wOption,
        card: studio.querySelector('[data-look-review-waistcoat]'),
        image: studio.querySelector('[data-look-review-waistcoat-img]'),
        name: studio.querySelector('[data-look-review-waistcoat-name]'),
        button: studio.querySelector('[data-look-review-waistcoat-btn]'),
        role: t(studio, 'selected_waistcoat', 'Waistcoat'),
        thumb: wThumbnail,
        colour: wColour
      }
    ];
    let reviewVisible = 0;
    for (const item of reviewItems) {
      const shade = item.option.dataset.shade || item.option.value;
      const available = item.show && Boolean(item.thumb || item.colour);
      if (available) reviewVisible += 1;
      if (item.card) item.card.hidden = !available;
      if (item.name) item.name.textContent = available ? shade : '';
      if (item.image) {
        if (available && item.thumb) {
          item.image.hidden = false;
          item.image.src = item.thumb;
          item.image.alt = t(studio, 'selected_scan_alt', `${item.option.value} ${item.role} fabric image`, {
            label: item.option.value,
            role: item.role
          });
          item.image.style.backgroundColor = '';
        } else if (available && item.colour) {
          item.image.hidden = false;
          item.image.removeAttribute('src');
          item.image.alt = shade;
          item.image.style.backgroundColor = item.colour;
        } else {
          item.image.hidden = true;
          item.image.removeAttribute('src');
          item.image.alt = '';
          item.image.style.backgroundColor = '';
        }
      }
      if (item.button) {
        item.button.disabled = !available || !item.option.dataset.scan;
        item.button.setAttribute(
          'aria-label',
          t(studio, 'review_enlarge', `Enlarge ${shade} fabric photo`, { shade })
        );
      }
    }
    const reviewColours = studio.querySelector('[data-look-review-colours]');
    if (reviewColours) reviewColours.hidden = reviewVisible === 0;
    for (const [select, selector] of [[k, '[data-look-kameez-palette]'], [w, '[data-look-waistcoat-palette]']]) {
      studio.querySelector(selector)?.querySelectorAll('.sf-look-studio__scan').forEach((button) => {
        button.setAttribute('aria-pressed', String(Number(button.dataset.index) === select.selectedIndex));
      });
    }
    const photo = studio.querySelector('[data-look-photo]');
    const scanPreview = studio.querySelector('[data-look-scan-preview]');
    const scanPreviewLink = studio.querySelector('[data-look-scan-preview-link]');
    const photoCaption = studio.querySelector('[data-look-photo-caption]');
    const focusQuality = studio.dataset.focusQuality || '';
    const modelKey = kOption.dataset.modelShade && wOption.dataset.modelShade
      ? `${kOption.dataset.modelShade}-${wOption.dataset.modelShade}` : null;
    const matchedPairModel = focusQuality &&
      kOption.dataset.quality === focusQuality && wOption.dataset.quality === focusQuality &&
      modelKey && studio.saraLookPhotos?.[modelKey];
    const suitOnlyModel = focusQuality &&
      kOption.dataset.quality === focusQuality && kOption.dataset.modelShade &&
      studio.saraSuitPhotos?.[kOption.dataset.modelShade];
    const selectedModel = needW ? matchedPairModel : suitOnlyModel;
    const scanOnlyPreview = (studio.dataset.devCheckout === 'true' || studio.dataset.visualPreview === 'true') && !selectedModel;
    const previewOption = needK ? kOption : wOption;
    const roleKameez = t(studio, 'piece_kameez', 'shalwar kameez');
    const roleWaistcoat = t(studio, 'piece_waistcoat', 'waistcoat');
    const selectedScans = [
      { option: kOption, link: studio.querySelector('[data-look-selected-kameez-link]'), image: studio.querySelector('[data-look-selected-kameez-scan]'), name: studio.querySelector('[data-look-selected-kameez-name]'), role: roleKameez, isKameez: true, show: previewK },
      { option: wOption, link: studio.querySelector('[data-look-selected-waistcoat-link]'), image: studio.querySelector('[data-look-selected-waistcoat-scan]'), name: studio.querySelector('[data-look-selected-waistcoat-name]'), role: roleWaistcoat, isKameez: false, show: needW }
    ];
    for (const item of selectedScans) {
      const src = item.option.dataset.scan;
      const available = item.show && Boolean(src);
      if (item.link) {
        item.link.hidden = !available;
        if (available) item.link.href = src;
      }
      if (item.image) {
        item.image.hidden = !available;
        if (available) {
          item.image.src = src;
          item.image.alt = t(studio, 'selected_scan_alt', `${item.option.value} ${item.role} fabric image`, { label: item.option.value, role: item.role });
        } else item.image.removeAttribute?.('src');
      }
      if (item.name) item.name.textContent = item.show
        ? `${item.option.value}${item.isKameez && !needK ? t(studio, 'styling_ref', ' · styling reference, not included') : ''}`
        : '';
    }
    if (photo) photo.hidden = scanOnlyPreview;
    if (scanPreviewLink) {
      scanPreviewLink.hidden = !scanOnlyPreview || !previewOption.dataset.scan;
      if (scanOnlyPreview && previewOption.dataset.scan) scanPreviewLink.href = previewOption.dataset.scan;
    }
    if (scanPreview) {
      scanPreview.hidden = !scanOnlyPreview || !previewOption.dataset.scan;
      if (scanOnlyPreview && previewOption.dataset.scan) {
        scanPreview.src = previewOption.dataset.scan;
        scanPreview.alt = t(studio, 'scan_preview_alt', `${previewOption.value} ${needK ? 'shalwar kameez' : 'waistcoat'} fabric image`, {
          label: previewOption.value,
          piece: needK ? roleKameez : roleWaistcoat
        });
      }
    }
    if (scanOnlyPreview && photoCaption) {
      photoCaption.textContent = !needW
        ? (kOption.dataset.scan
          ? t(studio, 'photo_suit_only_scan', `Shalwar kameez only: ${kOption.value}. This image shows the actual fabric; no waistcoat is included. A verified waistcoat-free model image is not available yet.`, { name: kOption.value })
          : t(studio, 'photo_suit_only_no_scan', `Shalwar kameez only: ${kOption.value}. A verified waistcoat-free model image and fabric image are not available yet; no waistcoat is included.`, { name: kOption.value }))
        : t(studio, 'photo_scan_only', `${needK ? 'Shalwar kameez' : 'Waistcoat'} fabric: ${previewOption.value}.`, {
            piece: needK ? roleKameez : roleWaistcoat,
            name: previewOption.value,
            extra: needK && needW
              ? t(studio, 'photo_scan_extra_both', `Waistcoat choice: ${wOption.value}; compare both fabric images.`, { waistcoat: wOption.value })
              : t(studio, 'photo_scan_extra_wc', 'Only the waistcoat fabric is included.')
          });
    } else if (photo && photoCaption && selectedModel) {
      photo.src = selectedModel;
      photo.alt = needW
        ? t(studio, 'photo_model_both_alt', `Model visualisation of ${kOption.value} shalwar kameez and ${wOption.value} waistcoat`, { kameez: kOption.value, waistcoat: wOption.value })
        : t(studio, 'photo_model_suit_alt', `Model visualisation wearing ${kOption.value} shalwar kameez without a waistcoat`, { kameez: kOption.value });
      photoCaption.textContent = !needW
        ? t(studio, 'photo_caption_suit', `Shalwar kameez-only visualisation for ${kOption.value}; no waistcoat is shown. The selected shalwar kameez fabric is included. Match is tied to this exact shade ID; compare the fabric image for the cloth itself. Unstitched fabric is supplied.`, { name: kOption.value })
        : needK
          ? t(studio, 'photo_caption_three', `Three-piece visualisation for ${kOption.value} shalwar kameez colour and ${wOption.value} waistcoat. Both fabrics are selected. Model colours are appearance-matched; physical lot identity and exact screen colour are not certified. Unstitched fabric is supplied.`, { kameez: kOption.value, waistcoat: wOption.value })
          : t(studio, 'photo_caption_wc_only', `Waistcoat-only styling visualisation for ${kOption.value} shalwar kameez colour and ${wOption.value} waistcoat. Only waistcoat fabric is included; the shalwar kameez colour is a preview reference. Model colours are appearance-matched; physical lot identity and exact screen colour are not certified. Unstitched fabric is supplied.`, { kameez: kOption.value, waistcoat: wOption.value });
    }
    if (photoCaption && studio.dataset.visualPreview === 'true' && selectedModel) {
      photoCaption.textContent = t(studio, 'visual_caption', 'Approximate colour visualisation—not an exact fabric transfer. Compare the fabric images below.');
    } else if (photoCaption && String(selectedModel || '').includes('sara-medora-ai-preview-')) {
      photoCaption.textContent += t(studio, 'ai_preview_note', ' AI-assisted approximate color visualization for this unpublished preview; the enlarged fabric images beside the model show your selections.');
    }

    const K_STD_M = 4.25;
    const W_STD_M = 1.25;
    const kMetres = custom ? Number(kLength.value) : K_STD_M;
    const wMetres = custom ? Number(wLength.value) : W_STD_M;
    const kCount = needK ? (kCountInput ? Number(kCountInput.value) : 1) : 0;
    const wCount = needW ? (wCountInput ? Number(wCountInput.value) : 1) : 0;
    const validCount = (input, count) => !input || (Number.isInteger(count) && count >= 0 && count <= Number(input.max));
    const activeK = needK && kCount > 0;
    const activeW = needW && wCount > 0;
    const kStandardPrice = Number(kOption.dataset.standardSuitPrice);
    const wStandardPrice = Number(wOption.dataset.standardWaistcoatPrice);
    // Per-metre rate from published suit / waistcoat cut prices (fallback: data-rate).
    const kRateListed = Number(kOption.dataset.rate);
    const wRateListed = Number(wOption.dataset.rate);
    const kRate = kStandardPrice > 0 ? kStandardPrice / K_STD_M : kRateListed;
    const wRate = wStandardPrice > 0 ? wStandardPrice / W_STD_M : wRateListed;
    const kIncrement = Number(studio.dataset.suitRounding);
    const wIncrement = Number(studio.dataset.waistcoatRounding);
    const kCustomIncrement = Number(kOption.dataset.customRounding) || 100;
    const wCustomIncrement = Number(wOption.dataset.customRounding) || 100;
    const validLength = (input, metres) => /^\d+(?:\.\d{1,2})?$/.test(input.value) && Number.isFinite(metres) && metres >= 0.01 && metres <= 100;
    const valid = (activeK || activeW) && validCount(kCountInput, kCount) && validCount(wCountInput, wCount) &&
      (!activeK || (!custom || validLength(kLength, kMetres))) && (!activeW || (!custom || validLength(wLength, wMetres))) &&
      Number.isFinite(kRate) && kRate > 0 && Number.isFinite(wRate) && wRate > 0 &&
      Number.isInteger(kCustomIncrement) && kCustomIncrement > 0 && Number.isInteger(wCustomIncrement) && wCustomIncrement > 0;
    error.hidden = valid;
    error.textContent = valid ? '' : t(studio, 'validation_qty', 'Choose at least one piece and enter a whole-number quantity between 0 and 999. Custom metres must be between 0.01 m and 100 m, to two decimal places.');
    enquiry.hidden = !valid;
    if (save) save.disabled = !valid;
    prices.replaceChildren();
    if (!valid) {
      summary.textContent = t(studio, 'validation_custom', 'Please check your custom lengths.');
      renderTray(studio);
      return;
    }

    const kStandard = isStandardCut(kMetres, K_STD_M);
    const wStandard = isStandardCut(wMetres, W_STD_M);
    const kUnitPrice = kStandard && kStandardPrice > 0 ? kStandardPrice :
      (kStandard ? roundDown(kMetres * kRate, kIncrement || kCustomIncrement) : roundUp(kMetres * kRate, kCustomIncrement));
    const wUnitPrice = wStandard && wStandardPrice > 0 ? wStandardPrice :
      (wStandard ? roundDown(wMetres * wRate, wIncrement || wCustomIncrement) : roundUp(wMetres * wRate, wCustomIncrement));
    const kPrice = activeK ? kUnitPrice * kCount : 0;
    const wPrice = activeW ? wUnitPrice * wCount : 0;
    const parts = [];
    if (activeK) parts.push(t(studio, 'summary_kameez', `${kCount} × ${kOption.value} shalwar kameez (${formatMetres(kMetres)} m each)`, { count: kCount, name: kOption.value, metres: formatMetres(kMetres) }));
    if (activeW) parts.push(t(studio, 'summary_waistcoat', `${wCount} × ${wOption.value} waistcoat (${formatMetres(wMetres)} m each)`, { count: wCount, name: wOption.value, metres: formatMetres(wMetres) }));
    summary.textContent = parts.join(' + ');
    const addPrice = (label, count, metres, rate, increment, unitPrice, standard) => {
      const row = document.createElement('p');
      row.textContent = standard
        ? t(studio, 'price_standard_row', `${label}: ${count} × standard ${formatMetres(metres)} m cut → ${money(unitPrice * count)}`, { label, count, metres: formatMetres(metres), amount: money(unitPrice * count) })
        : t(studio, 'price_custom_row', `${label}: ${count} × ${formatMetres(metres)} m × ${money(rate)}/m → ${money(unitPrice * count)} (custom cut rounded up to ${money(increment)} per cut)`, { label, count, metres: formatMetres(metres), rate: money(rate), amount: money(unitPrice * count), increment: money(increment) });
      prices.appendChild(row);
    };
    if (activeK) addPrice(t(studio, 'price_kameez', 'Shalwar kameez fabric'), kCount, kMetres, kRate, kCustomIncrement, kUnitPrice, kStandard);
    if (activeW) addPrice(t(studio, 'price_waistcoat', 'Waistcoat fabric'), wCount, wMetres, wRate, wCustomIncrement, wUnitPrice, wStandard);
    const total = document.createElement('strong');
    const totalLabel = activeK && activeW
      ? t(studio, 'price_three_piece', 'Three-piece fabric total')
      : t(studio, 'price_estimated', 'Estimated fabric total');
    total.textContent = `${totalLabel}: ${money(kPrice + wPrice)}`;
    prices.appendChild(total);
    const cutLabel = custom ? t(studio, 'wa_cut_custom', 'custom length') : t(studio, 'wa_cut_standard', 'standard length');
    const lines = [
      t(studio, 'wa_intro_one', 'Hello Sara Fabrics, please check whether the selected unstitched fabric is currently available before I place an order:'),
      '',
      t(studio, 'wa_heading', 'SELECTED FABRIC ORDER ENQUIRY'),
      activeK ? t(studio, 'wa_line_kameez', `1. Shalwar kameez fabric — ${kOption.value}
   Quantity: ${kCount} × ${formatMetres(kMetres)} m each
   Selected-fabric amount: ${money(kPrice)}`, { n: 1, name: kOption.value, count: kCount, metres: formatMetres(kMetres), amount: money(kPrice) }) : null,
      activeW ? t(studio, 'wa_line_waistcoat', `${activeK ? '2' : '1'}. Waistcoat fabric — ${wOption.value}
   Quantity: ${wCount} × ${formatMetres(wMetres)} m each
   Selected-fabric amount: ${money(wPrice)}`, { n: activeK ? 2 : 1, name: wOption.value, count: wCount, metres: formatMetres(wMetres), amount: money(wPrice) }) : null,
      !activeK && activeW ? t(studio, 'wa_styling_ref', `Styling reference only (not included): ${kOption.value} shalwar kameez colour`, { name: kOption.value }) : null,
      t(studio, 'wa_estimated', `Estimated fabric total: ${money(kPrice + wPrice)}`, { amount: money(kPrice + wPrice) }),
      t(studio, 'wa_outro', `Cut requested: ${cutLabel}. Please confirm availability for each selected quality and shade, then confirm exact shade, cutting, final price and delivery details. This message is an enquiry, not a confirmed order.`, { cut: cutLabel })
    ].filter(Boolean);
    enquiry.href = quoteUrl(enquiry.dataset.baseHref, lines);

    const draft = {
      pieces, custom, kValue: k.value, wValue: w.value,
      kCount, wCount, kMetres, wMetres,
      summary: parts.join(' + '), total: kPrice + wPrice,
      photo: scanOnlyPreview ? (scanPreview && !scanPreview.hidden ? scanPreview.src : null) : (photo?.src || null),
      photoIsScan: scanOnlyPreview, cartItems: null
    };
    studio.saraDraft = draft;

    const checkoutHosts = new Set([
      'sara-fabrics-winter-2026-checkout-lab.myshopify.com',
      'shop.sarafabrics.com',
      'sarafabrics.myshopify.com'
    ]);
    if (cart && studio.dataset.devCheckout === 'true' && checkoutHosts.has(location.hostname) && !custom) {
      const cutItem = (option, piece, metres, count, unitPrice, variantKey, priceKey, availableKey) => {
        const id = Number(option.dataset[variantKey]);
        const variantPrice = Number(option.dataset[priceKey]);
        const stockKey = availableKey === 'skAvailable' ? 'skStock' : 'wcStock';
        const stock = Number(option.dataset[stockKey]);
        if (!Number.isSafeInteger(id) || id <= 0 || variantPrice !== unitPrice * 100 || option.dataset[availableKey] !== 'true' || !Number.isInteger(stock) || stock < count) return null;
        return { id, quantity: count, properties: { Fabric: option.dataset.quality, Shade: option.dataset.shade, Cut: `${formatMetres(metres)} m`, _piece: piece, ...(!activeK && piece === 'waistcoat' ? { 'Shalwar kameez styling reference (not included)': kOption.value } : {}) } };
      };
      const items = [
        activeK ? cutItem(kOption, 'shalwar kameez', 4.25, kCount, kUnitPrice, 'skVariantId', 'skPriceCents', 'skAvailable') : null,
        activeW ? cutItem(wOption, 'waistcoat', 1.25, wCount, wUnitPrice, 'wcVariantId', 'wcPriceCents', 'wcAvailable') : null
      ].filter(Boolean);
      if (items.length === Number(activeK) + Number(activeW)) {
        draft.cartItems = items;
        if (!studio.querySelector('[data-look-tray-list]')) {
          studio.saraCartItems = items;
          cart.hidden = false;
        }
      }
    }
    renderTray(studio);
  };

  const init = () => {
    document.querySelectorAll('[data-sara-look-studio]').forEach((studio) => {
      if (studio.dataset.lookReady) return;
      studio.dataset.lookReady = 'true';
      const photoManifest = studio.querySelector('[data-look-photos]');
      try {
        if (photoManifest) studio.saraLookPhotos = JSON.parse(photoManifest.textContent);
      } catch (err) {
        console.error('Sara Look Studio: look photo manifest JSON failed', err);
        studio.saraLookPhotos = {};
      }
      const suitPhotoManifest = studio.querySelector('[data-look-suit-photos]');
      try {
        if (suitPhotoManifest) studio.saraSuitPhotos = JSON.parse(suitPhotoManifest.textContent);
      } catch (err) {
        console.error('Sara Look Studio: suit photo manifest JSON failed', err);
        studio.saraSuitPhotos = {};
      }
      studio.saraSavedLooks = [];
      renderPalette(studio, studio.querySelector('[data-look-kameez]'), '[data-look-kameez-palette]');
      renderPalette(studio, studio.querySelector('[data-look-waistcoat]'), '[data-look-waistcoat-palette]');
      const overlay = studio.querySelector('[data-look-scan-dialog]');
      studio.querySelector('[data-look-scan-close]')?.addEventListener('click', () => closeScan(studio));
      overlay?.addEventListener('click', (event) => { if (event.target === overlay) closeScan(studio); });
      overlay?.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') closeScan(studio);
        if (event.key === 'Tab') {
          event.preventDefault();
          studio.querySelector('[data-look-scan-close]')?.focus?.();
        }
      });
      [
        { linkSelector: '[data-look-scan-preview-link]', optionSelector: null },
        { linkSelector: '[data-look-selected-kameez-link]', optionSelector: '[data-look-kameez]' },
        { linkSelector: '[data-look-selected-waistcoat-link]', optionSelector: '[data-look-waistcoat]' },
        { linkSelector: '[data-look-review-kameez-btn]', optionSelector: '[data-look-kameez]' },
        { linkSelector: '[data-look-review-waistcoat-btn]', optionSelector: '[data-look-waistcoat]' }
      ].forEach(({ linkSelector, optionSelector }) => {
        const link = studio.querySelector(linkSelector);
        if (!link) return;
        const selectedOption = () => optionSelector
          ? studio.querySelector(optionSelector)?.selectedOptions[0]
          : studio.querySelector(studio.querySelector('[data-look-pieces]:checked')?.value === 'waistcoat' ? '[data-look-waistcoat]' : '[data-look-kameez]')?.selectedOptions[0];
        link.addEventListener('click', (event) => {
          const option = selectedOption();
          openScan(studio, option?.dataset.scan, option?.value, event.currentTarget, event);
        });
        link.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'touch') return;
          const option = selectedOption();
          showScanHover(studio, option?.dataset.scan, option?.value);
        });
        link.addEventListener('pointerleave', () => hideScanHover(studio));
      });
      studio.querySelectorAll('select, input').forEach((input) => {
        input.addEventListener('change', () => update(studio));
        if (input.type === 'number') input.addEventListener('input', () => update(studio));
      });
      studio.addEventListener?.('click', (event) => {
        const step = event.target.closest?.('[data-look-step]');
        if (!step || step.disabled) return;
        const input = step.parentElement?.querySelector('input[type="number"]');
        if (!input || input.disabled) return;
        if (step.dataset.lookStep === '1') input.stepUp();
        else input.stepDown();
        input.dispatchEvent(new Event('input', { bubbles: true }));
      });
      studio.querySelector('[data-look-save]')?.addEventListener('click', () => {
        update(studio);
        const draft = studio.saraDraft;
        if (!draft) return;
        const looks = studio.saraSavedLooks;
        const editing = studio.saraEditingIndex;
        const duplicate = looks.findIndex((look, index) => index !== editing && lookKey(look) === lookKey(draft));
        const error = studio.querySelector('[data-look-error]');
        if (duplicate >= 0) {
          error.textContent = t(studio, 'save_duplicate', `This combination is already saved as look ${duplicate + 1}. Edit its colours or counts before saving again.`, { n: duplicate + 1 });
          error.hidden = false;
          return;
        }
        if (editing === undefined && looks.length >= 20) {
          error.textContent = t(studio, 'save_limit', 'You can save up to 20 looks in one visit. Remove a look to add another.');
          error.hidden = false;
          return;
        }
        if (editing === undefined) looks.push(draft);
        else looks[editing] = draft;
        studio.saraEditingIndex = undefined;
        setActionLabel(studio.querySelector('[data-look-save]'), t(studio, 'save_another', 'Save another colour combination'));
        studio.querySelector('[data-look-save-feedback]').textContent = t(studio, 'save_feedback', `Look ${editing === undefined ? looks.length : editing + 1} saved. Change colours to make another, or review your saved looks below.`, { n: editing === undefined ? looks.length : editing + 1 });
        error.hidden = true;
        renderTray(studio);
      });
      studio.querySelector('[data-look-add-more]')?.addEventListener('click', () => {
        studio.saraEditingIndex = undefined;
        setActionLabel(studio.querySelector('[data-look-save]'), t(studio, 'save_another', 'Save another colour combination'));
        studio.querySelector('[data-look-save-feedback]').textContent = t(studio, 'save_next_hint', 'Change the fabrics above, then save your next look.');
        studio.querySelector('[data-look-kameez]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      studio.querySelector('[data-look-tray-list]')?.addEventListener('click', (event) => {
        const button = event.target.closest('[data-look-action]');
        if (!button) return;
        const index = Number(button.dataset.lookIndex);
        const looks = studio.saraSavedLooks;
        if (!Number.isInteger(index) || index < 0 || index >= looks.length) return;
        if (button.dataset.lookAction === 'remove') {
          looks.splice(index, 1);
          if (studio.saraEditingIndex === index) {
            studio.saraEditingIndex = undefined;
            setActionLabel(studio.querySelector('[data-look-save]'), t(studio, 'save_another', 'Save another colour combination'));
            studio.querySelector('[data-look-save-feedback]').textContent = t(studio, 'edit_removed', 'The look being edited was removed. Change the fabrics above to save a new look.');
          } else if (studio.saraEditingIndex > index) {
            studio.saraEditingIndex -= 1;
            setActionLabel(studio.querySelector('[data-look-save]'), t(studio, 'update_look', `Update look ${studio.saraEditingIndex + 1}`, { n: studio.saraEditingIndex + 1 }));
            studio.querySelector('[data-look-save-feedback]').textContent = t(studio, 'editing_look', `Editing look ${studio.saraEditingIndex + 1}. Save your changes to update it.`, { n: studio.saraEditingIndex + 1 });
          }
          renderTray(studio);
          return;
        }
        if (button.dataset.lookAction !== 'edit') return;
        const look = looks[index];
        studio.saraEditingIndex = index;
        studio.querySelector('[data-look-kameez]').value = look.kValue;
        studio.querySelector('[data-look-waistcoat]').value = look.wValue;
        studio.querySelector('[data-look-kameez-count]').value = String(look.kCount);
        studio.querySelector('[data-look-waistcoat-count]').value = String(look.wCount);
        studio.querySelector('[data-look-kameez-length]').value = String(look.kMetres);
        studio.querySelector('[data-look-waistcoat-length]').value = String(look.wMetres);
        studio.querySelectorAll('[data-look-pieces]').forEach((radio) => { radio.checked = radio.value === look.pieces; });
        studio.querySelectorAll('[data-look-cut]').forEach((radio) => { radio.checked = radio.value === (look.custom ? 'custom' : 'standard'); });
        setActionLabel(studio.querySelector('[data-look-save]'), t(studio, 'update_look', `Update look ${index + 1}`, { n: index + 1 }));
        studio.querySelector('[data-look-save-feedback]').textContent = t(studio, 'editing_look', `Editing look ${index + 1}. Save your changes to update it.`, { n: index + 1 });
        update(studio);
        studio.querySelector('[data-look-kameez]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      studio.querySelector('[data-look-cart]')?.addEventListener('click', async (event) => {
        const button = event.currentTarget;
        const error = studio.querySelector('[data-look-tray-error]') || studio.querySelector('[data-look-error]');
        update(studio);
        if (!studio.saraCartItems || button.disabled || studio.saraSubmitting) {
          if (!studio.saraSubmitting) {
            error.textContent = studio.querySelector('[data-look-tray-note]')?.textContent || t(studio, 'cart_unavailable', 'These test cuts are not available for cart checkout.');
            error.hidden = false;
          }
          return;
        }
        const items = studio.saraCartItems;
        studio.saraSubmitting = true;
        button.disabled = true;
        error.hidden = true;
        try {
          const cartRoot = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
          const response = await fetch(`${cartRoot}cart/add.js`, {
            method: 'POST', credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items })
          });
          if (!response.ok || response.redirected && new URL(response.url).pathname === '/password') {
            const failure = await response.json().catch(() => null);
            throw new Error(failure?.description || failure?.message || t(studio, 'cart_failed', 'The test cart could not accept those cuts. Please check variant availability and storefront access.'));
          }
          window.location.assign(`${cartRoot}cart`);
        } catch (failure) {
          error.textContent = failure.message;
          error.hidden = false;
          studio.saraSubmitting = false;
          button.disabled = false;
        }
      });
      update(studio);
    });
  };

  const initMediaSwaps = () => {
    if (window.SaraMediaSwap && typeof window.SaraMediaSwap.bindAll === 'function') {
      window.SaraMediaSwap.bindAll(document);
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => { init(); initMediaSwaps(); });
  else { init(); initMediaSwaps(); }
  document.addEventListener('shopify:section:load', () => { init(); initMediaSwaps(); });
})();
